import { useState, useEffect, useRef } from 'react';

interface UseMinimumLoaderOptions {
  minimumDuration?: number; // in milliseconds
  initialLoading?: boolean;
}

/**
 * Custom hook that ensures a loader stays visible for a minimum duration
 * to provide better user feedback and prevent flickering.
 *
 * @param isLoading - The current loading state
 * @param options - Configuration options
 * @returns boolean - Whether to show the loader
 *
 * @example
 * const showLoader = useMinimumLoader(loading, { minimumDuration: 1000 });
 * if (showLoader) {
 *   return <Loader message="Loading..." />;
 * }
 */

export const useMinimumLoader = (isLoading: boolean, options: UseMinimumLoaderOptions = {}) => {
  const { minimumDuration = 800, initialLoading = true } = options;
  const [showLoader, setShowLoader] = useState(initialLoading);
  const startTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      startTimeRef.current = Date.now();
      setShowLoader(true);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else {
      if (startTimeRef.current) {
        const elapsedTime = Date.now() - startTimeRef.current;
        const remainingTime = Math.max(0, minimumDuration - elapsedTime);

        if (remainingTime > 0) {
          // Set timeout to hide loader after minimum duration
          timeoutRef.current = setTimeout(() => {
            setShowLoader(false);
          }, remainingTime);
        } else {
          // Minimum duration already passed, hide immediately
          setShowLoader(false);
        }
      } else {
        // No start time recorded, hide immediately
        setShowLoader(false);
      }
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, minimumDuration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return showLoader;
};
